import { Component, h, Prop, State, Element } from '@stencil/core';

const COMPONENT_NAME = 'sn-hero-header';

/**
 * Two-column hero section with content on one side and media on the other.
 * Left column contains heading, description, and action buttons; right column shows media (image, video, or YouTube).
 * Content position can be reversed via prop. Differs from type-4 by using a contained layout.
 *
 * @category Hero Headers
 * @slot heading - {required}, <me-heading variant="1" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Heading slot for main title
 * @slot description - {required}, <me-text tag="p" variant="large"> - Description slot for subtitle or lead text
 * @slot media - {required}, <me-video-youtube lazy="false">, <me-picture lazy="false">, <me-video lazy="false"> - Media slot for video or image
 * @slot action-primary - {required}, <me-button variant="primary">, <form-subscribe-type-1> - Primary action slot
 * @slot action-secondary - <me-button variant="secondary"> - Secondary action slot
 * @example
 *   <sn-hero-header>
 *     <me-heading slot="heading" variant="1" tag="h1">Simplify complexity: optimize, scale, excel</me-heading>
 *     <me-text slot="description" tag="p" variant="large">Upgrade your productivity with tools crafted to simplify challenges and fuel growth. Refine processes, scale smoothly, and accomplish your goals faster with our advanced solution.</me-text>
 *     <me-picture slot="media" src="https://images.unsplash.com/photo-1504548840739-580b10ae7715?q=80&w=3000&auto=format&fit=crop" lazy="false" alt="Example image"></me-picture>
 *     <me-button slot="action-primary" variant="primary">Get started</me-button>
 *     <me-button slot="action-secondary" variant="secondary">Contact Sales</me-button>
 *   </sn-hero-header>
 */
@Component({
  tag: 'sn-hero-header',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHeroHeader {
  @Element() hostEl!: HTMLElement;

  /**
   * Position of the content block: 'left' or 'right'.
   * If 'left', content is on the left and media is on the right.
   * If 'right', content is on the right and media is on the left.
   * @default left
   */
  @Prop() contentPosition: 'left' | 'right' = 'left';

  @State() hasSecondaryAction: boolean = false;

  componentWillLoad() {
    this.checkSecondaryAction();
  }

  componentDidLoad() {
    this.checkSecondaryAction();
  }

  private checkSecondaryAction() {
    const secondaryActionElements = this.hostEl.querySelectorAll('[slot="action-secondary"]');
    this.hasSecondaryAction = secondaryActionElements.length > 0;
  }

  render() {
    return (
      <me-container class={COMPONENT_NAME} yGap="2xl" yGapLg="4xl">
        <me-row xGapLg="3xl" yGap="md" yGapLg="4xl" align="center" justify="center" direction={this.contentPosition === 'right' ? 'row-reverse' : 'row'}>
          <me-column spanLg="6" span="12">
            <slot name="heading"></slot>
            <slot name="description"></slot>
            <me-row xGap="md" xGapLg="md" justify="center" yGap="none" yGapLg="md" class={`${COMPONENT_NAME}__actions-group`}>
              <me-column span="auto">
                <slot name="action-primary"></slot>
              </me-column>
              {this.hasSecondaryAction && (
                <me-column span="auto">
                  <slot name="action-secondary"></slot>
                </me-column>
              )}
            </me-row>
          </me-column>
          <me-column spanLg="6" span="12">
            <slot name="media"></slot>
          </me-column>
        </me-row>
      </me-container>
    );
  }
}
